'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

/**
 * Trailing-edge debounce of `value`, plus a setter that applies a value at once and discards the
 * pending update.
 *
 * `maxWaitMs` applies the latest value at most `maxWaitMs` after the first pending change, even
 * while `value` keeps changing.
 *
 * Replaces `useDebounceValue` from `usehooks-ts`, which leaks its in-flight timer: it builds one
 * `lodash.debounce` instance for the call path and a second one for the unmount cleanup, so the
 * scheduled update survives unmount and sets state on a torn-down tree.
 *
 * `value` is compared by identity, so pass a primitive or a referentially stable value (e.g. React
 * state): a fresh object every render re-arms the timer every render and never settles.
 */
export function useDebouncedValue(value, delayMs, t0) {
  "use memo";

  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    maxWaitMs
  } = t1;
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);
  const pendingSinceRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingSinceRef.current = null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cancelPendingUpdate = t2;
  let t3;
  let t4;
  if ($[3] !== delayMs || $[4] !== maxWaitMs || $[5] !== value) {
    t3 = () => {
      const now = Date.now();
      if (pendingSinceRef.current === null) {
        pendingSinceRef.current = now;
      }
      const delay = maxWaitMs === undefined ? delayMs : Math.max(0, Math.min(delayMs, pendingSinceRef.current + maxWaitMs - now));
      const timeout = setTimeout(() => {
        timeoutRef.current = null;
        pendingSinceRef.current = null;
        setDebouncedValue(value);
      }, delay);
      timeoutRef.current = timeout;
      return () => {
        clearTimeout(timeout);
        timeoutRef.current = null;
      };
    };
    t4 = [value, delayMs, maxWaitMs];
    $[3] = delayMs;
    $[4] = maxWaitMs;
    $[5] = value;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = nextValue => {
      cancelPendingUpdate();
      setDebouncedValue(nextValue);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const setValueNow = t5;
  let t6;
  if ($[9] !== debouncedValue) {
    t6 = [debouncedValue, setValueNow];
    $[9] = debouncedValue;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}